import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { api, ApiError } from '@/lib/api';

const schema = z
  .object({
    newPassword: z.string().min(8, 'At least 8 characters'),
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, {
    message: 'Passwords must match',
    path: ['confirm'],
  });

type FormValues = z.infer<typeof schema>;

/**
 * Choose a password from an emailed reset or invite link. The link's token
 * only sets a password; afterwards the user signs in, with MFA if it is on.
 */
export function ChangePassword() {
  const navigate = useNavigate();
  const token = useSearchParams()[0].get('token');
  const change = useMutation({
    mutationFn: (input: { token: string; newPassword: string }) =>
      api.post<void>('/api/Clients/reset-password', input),
    onSuccess: () =>
      navigate('/login', {
        replace: true,
        state: { notice: 'Password set. Sign in with your new password.' },
      }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirm: '' },
  });

  const onSubmit = handleSubmit((values) => {
    if (token) change.mutate({ token, newPassword: values.newPassword });
  });

  const errorMessage =
    change.error instanceof ApiError
      ? change.error.message
      : change.error
        ? 'Could not update password.'
        : null;

  return (
    <div className="w-full max-w-md">
      <h1 className="font-display text-h1 font-bold">Choose a new password</h1>
      <p className="mt-3 text-body text-text-secondary">
        You'll sign in again after this, on every device.
      </p>

      {token ? (
        <form onSubmit={onSubmit} className="mt-10 flex flex-col gap-gap-element-lg">
          <Field id="newPassword" label="New password" error={errors.newPassword?.message}>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              {...register('newPassword')}
            />
          </Field>

          <Field id="confirm" label="Confirm password" error={errors.confirm?.message}>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              {...register('confirm')}
            />
          </Field>

          {errorMessage ? (
            <div className="rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-body-sm text-danger">
              {errorMessage}
            </div>
          ) : null}

          <Button type="submit" size="lg" disabled={change.isPending}>
            {change.isPending ? 'Saving…' : 'Update password'}
          </Button>
        </form>
      ) : (
        <p className="mt-10 text-body text-text-secondary">
          This link is invalid or has expired.{' '}
          <Link to="/reset" className="font-medium text-accent underline">
            Request a new one
          </Link>
          .
        </p>
      )}
    </div>
  );
}
